import { useState } from "react"

import { faIcon } from "@utils/faIcon"

import { Avatar, Button } from "@atoms/index"

import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import type { ScannedCode } from "@pages/Pricebook/components/CodeScanner/CodeScanner.types"
import CodeScannerDialog from "@pages/Pricebook/components/CodeScannerDialog/CodeScannerDialog"
import cleanBarcode, { BARCODE_MAX_LENGTH } from "@pages/Pricebook/components/CodeScannerDialog/cleanBarcode"
import useBarcodeLookupQuery from "@pages/Pricebook/components/CodeScannerDialog/useBarcodeLookupQuery"

import { RegisterCodeDialogProps } from "./RegisterCodeDialog.types"

// The scanner in capture mode: the camera keeps running, the last code read waits under the viewfinder with
// its registration status, and Register hands it to the field.
function RegisterCodeDialog(props: RegisterCodeDialogProps) {
    const { isOpen, onOpenChange, onRegister, pricebookItemId } = props

    const [scannedCode, setScannedCode] = useState("")

    const ownerQuery = useBarcodeLookupQuery(scannedCode)
    const owner = ownerQuery.data ?? null
    const isRegisteredToAnotherItem = owner !== null && owner.id !== pricebookItemId
    const isTooLong = scannedCode.length > BARCODE_MAX_LENGTH

    const handleOpenChange = (nextIsOpen: boolean) => {
        onOpenChange(nextIsOpen)

        if (!nextIsOpen) {
            setScannedCode("")
        }
    }

    const handleCode = (code: ScannedCode) => {
        const cleanedCode = cleanBarcode(code.value)

        if (cleanedCode !== "") {
            setScannedCode(cleanedCode)
        }
    }

    const handleRegister = () => {
        onRegister(scannedCode)
        handleOpenChange(false)
    }

    let caption = null

    if (isRegisteredToAnotherItem) {
        caption = <ListItem.Caption caption={`Already registered to ${owner.description}`} state="warning" />
    } else if (isTooLong) {
        caption = (
            <ListItem.Caption
                caption={`Too long to register (${BARCODE_MAX_LENGTH} characters max)`}
                state="warning"
            />
        )
    } else if (ownerQuery.isError) {
        // The API still rejects a duplicate on save, so registering stays possible
        caption = <ListItem.Caption caption="Couldn't check whether this code is already registered" />
    }

    return (
        <CodeScannerDialog
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            title="Part code registration"
            onCode={handleCode}
            result={
                scannedCode !== "" && (
                    <ListItem>
                        <ListItem.AvatarAndContent
                            avatar={
                                <Avatar
                                    variant="object"
                                    size="lg"
                                    icon={faIcon({ name: "qrcode", style: "regular" })}
                                />
                            }
                            content={
                                <ListItemContent
                                    leftLabel={<ListItem.Label label={scannedCode} truncate={true} />}
                                    leftCaption={caption}
                                />
                            }
                        />
                    </ListItem>
                )
            }
            actions={
                <Button
                    type="button"
                    variant="solid"
                    size="lg"
                    onClick={handleRegister}
                    isDisabled={scannedCode === "" || isRegisteredToAnotherItem || isTooLong || ownerQuery.isLoading}
                    noDebounce={true}
                >
                    Register
                </Button>
            }
        />
    )
}

export default RegisterCodeDialog
